<script setup lang="ts">
import type { PropType } from 'vue';
import type Housing from '~/models/Housing';
import type Product from '~/models/Product';

type ProductOption = {
  label: string;
  value: number;
};

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const emit = defineEmits<{
  (event: 'close'): void;
}>();

defineProps({
  housing: {
    type: Object as PropType<Housing>,
    required: true,
  },
});

const { trackBeginCheckout, trackPurchase } = useGoogleTagManager();
const { systemAPI, accountAPI } = useBackendRequests();
const staticLinks = useStaticLinks();
const { showToast } = useToast();
const isDesktop = useIsDesktop();
const formSubmitting = ref(false);
const options = ref<string[]>([]);

const { getTrackingConfig } = useTrackingConfig();
const trackingButton = getTrackingConfig('sale', 'buttons', 'multiPortalPublishing');
const trackingElement = getTrackingConfig('sale', 'elements','multiPortalPublishing');

const { t } = useI18n({
  useScope: 'local',
});

const { data: availableProducts, error: availableProductsError } = await useAsyncData<Product[]>('get-available-products-for-purchase-view', async () => {
  const response = await systemAPI.getActiveProductsByKey('multi-portal-publishing');
  return response.data;
});

if (availableProductsError.value) {
  throw availableProductsError;
}

if (Array.isArray(availableProducts.value) && availableProducts.value.length > 0) {
  console.log('availableProducts.value', availableProducts.value);
  options.value = availableProducts.value.map((product: Product) =>
    t('xxx-days-for-x-currency', {
      days: product.period_in_days,
      price: product.priceGross,
      currency: '€',
    }),
  );
} else {
  throw createError({
    statusCode: 500,
    fatal: true,
  });
}

const preselectedProductIndex = 1;
const preselectedProductId = availableProducts.value[preselectedProductIndex].id;

const loading = ref(false);
const selectedProductId = ref<number | undefined>(preselectedProductId);
const selectedProduct = computed(() => availableProducts.value!.find((product: Product) => product.id === selectedProductId.value));
const cmpIsLoaded = ref(false);
const shouldExecuteTrackBeginCheckoutEvent = computed(() => {
  return selectedProductId.value !== null && cmpIsLoaded.value;
});

const productOptions = computed(() =>
  (availableProducts.value || []).map((product: Product) => ({
    label: t('xxx-days-for-x-currency', {
      days: product.period_in_days,
      price: product.priceGross,
      currency: '€',
    }),
    value: product.id,
  }))
);

const selectedProductLabel = computed(() => {
  const selected = availableProducts.value?.find((product: Product) => product.id === selectedProductId.value);
  return selected
    ? t('xxx-days-for-x-currency', {
      days: selected.period_in_days,
      price: selected.priceGross,
      currency: '€',
    })
    : '';
});

const dropdownValues = computed(() => productOptions.value.map((option: ProductOption) => option.label));

const onSubmit = async () => {
  if (!selectedProductId.value) {
    return;
  }

  try {
    const response = await accountAPI.purchase([selectedProductId.value]);

    if (!response.data.redirectUrl) {
      throw new Error('No redirect URL');
    }

    const product = selectedProduct.value!;

    trackPurchase({
      currency: 'EUR',
      value: product.priceNet,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.key,
          price: product.priceNet,
          variant: product.period_in_days,
        },
      ],
    });

    await navigateTo(response.data.redirectUrl, {
      external: true,
    });

    emit('close');
  } catch (error) {
    console.error(error);
    showToast('error', t('unknown-error'), t('unknown-error-message'));
  }
};

onMounted(() => {
  checkForCmpIsLoaded();
});

const checkForCmpIsLoaded = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push = (function (originalPush) {
    return function () {
      let args = arguments[0];
      if (args.event === 'cmpEventLoadFinished') {
        setTimeout(() => (cmpIsLoaded.value = true), 0);
      }
      return originalPush.apply(window.dataLayer, [...arguments]);
    };
  })(window.dataLayer.push);
  if (window.dataLayer.some((item) => item.event === 'cmpEventLoadFinished')) {
    cmpIsLoaded.value = true;
  }
};

watch(shouldExecuteTrackBeginCheckoutEvent, (shouldExecute) => {
  if (shouldExecute) {
    trackBeginCheckoutAfterFetchedProducts();
  }
});

const trackBeginCheckoutAfterFetchedProducts = () => {
  const product = selectedProduct.value!;
  trackBeginCheckout({
    currency: 'EUR',
    value: product.priceNet,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_category: product.key,
        price: product.priceNet,
        variant: product.period_in_days,
      },
    ],
  });
};

const onSelectChange = (selectedLabel: string) => {
  const selectedOption = productOptions.value.find((option: ProductOption) => option.label === selectedLabel);
  if (selectedOption) {
    selectedProductId.value = selectedOption.value;
  }
};
</script>

<template>
  <div
    id="multi-portal-publishing"
    class="tw-tracking"
    :data-tracking-id="trackingElement.dataTrackingId"
    :data-page="trackingElement.dataPage"
    :data-headline="t('text_start')"
  >
    <h1>{{ t('text_start') }}</h1>
    <template v-if="loading">
      <Icon name="line-md:loading-twotone-loop" />
    </template>
    <header>
      <div>
        <img class="image" src="/assets/images/multi-portal/multi-portal-publishing-partnerlogos.png" :alt="t('publication')" />
      </div>
      <div class="listing">
        <ul>
          <li><Icon name="bi:check" />{{ t('more-reach') }}</li>
          <li><Icon name="bi:check" />{{ t('guaranteed-visibility') }}</li>
          <li><Icon name="bi:check" />{{ t('convenient') }}</li>
        </ul>
      </div>
    </header>

    <template v-if="!loading">
      <div class="main">
        <div class="item">
          <div>
            <h2>{{ t('ten-times-reach') }}</h2>
            <p>{{ t('ten-times-reach-text') }}</p>
          </div>
          <div>
            <img src="/assets/images/multi-portal/mehr_reichweite_1x.gif" :alt="t('visbility')" />
          </div>
        </div>
        <div class="item">
          <div>
            <h2>{{ t('visibility') }}</h2>
            <p>{{ t('visibility-text') }}</p>
          </div>
          <div>
            <img src="/assets/images/multi-portal/garantierte_sichtbarkeit_1x.gif" :alt="t('visbility')" />
          </div>
        </div>
        <div class="item">
          <div>
            <h2>{{ t('convenient') }}</h2>
            <p>{{ t('convenient-text') }}</p>
          </div>
          <div>
            <img src="/assets/images/multi-portal/einfach_und_bequem_1x.gif" :alt="t('visbility')" />
          </div>
        </div>
        <div class="item">
          <div>
            <h2>{{ t('largest-exchange-network') }}</h2>
            <p>{{ t('largest-exchange-network-text') }}</p>
          </div>
          <div>
            <img src="/assets/images/multi-portal/deutschlands-groesstes-tauschnetzwerk.svg" :alt="t('visbility')" />
          </div>
        </div>
        <div class="form" v-if="isDesktop">
          <form @submit.prevent="onSubmit">
            <tw-ui-select
              :disabled="formSubmitting"
              :values="dropdownValues"
              :value="selectedProductLabel"
              trigger-event="change"
              @update:value="onSelectChange"
            />
            <tw-ui-button
              type="submit"
              :disabled="formSubmitting"
              class="tw-tracking"
              :data-page="trackingButton.dataPage"
              :data-headline="t('text_start')"
              :data-tracking-id="trackingButton.dataTrackingId"
              :data-button-text="t('buy-now')"
              data-button-color="primary"
            >
              {{ t('buy-now') }}
            </tw-ui-button>
          </form>
          <div>
            <i18n-t tag="p" keypath="accept_terms_of_use" class="terms">
              <template #url>
                <nuxt-link :to="staticLinks.agb" target="_blank">{{ t('terms') }}</nuxt-link>
              </template>
            </i18n-t>
          </div>
        </div>
        <div class="faq">
          <tw-multi-portal-purchase-faq />
        </div>
        <div class="form" v-if="!isDesktop">
          <form @submit.prevent="onSubmit">
            <tw-ui-select
              :disabled="formSubmitting"
              :values="options"
              :value="selectedProduct?.period_in_days"
              trigger-event="change"
              @update:value="onSelectChange($event)"
            />
            <tw-ui-button
              type="submit"
              :disabled="formSubmitting"
              class="tw-tracking"
              :data-page="trackingButton.dataPage"
              :data-headline="t('text_start')"
              :data-tracking-id="trackingButton.dataTrackingId"
              :data-button-text="t('buy-now')"
              data-button-color="primary"
            >
              {{ t('buy-now') }}
            </tw-ui-button>
          </form>
          <div>
            <i18n-t tag="p" keypath="accept_terms_of_use" class="terms">
              <template #url>
                <nuxt-link :to="staticLinks.agb" target="_blank">{{ t('terms') }}</nuxt-link>
              </template>
            </i18n-t>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/tw-variables.scss';
@import '@/assets/styles/tw-mixins.scss';

#multi-portal-publishing {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;

  header,
  .main .item {
    display: flex;
    align-items: center;
    flex-direction: column;
    @include tw-is-desktop {
      flex-direction: row;
      justify-content: left;
      gap: 2rem;
    }
  }

  .listing {
    ul {
      li {
        display: flex;
        flex-direction: row;
        align-items: center;
      }
    }
    @include tw-is-desktop {
      font-size: 1.2rem;
    }
  }

  h1 {
    text-align: center;
    margin: 0;
    font-size: 1.2rem;
    line-height: 1.4rem;
    @include tw-is-desktop {
      font-size: 1.6rem;
      line-height: 1.6rem;
    }
  }

  .image {
    width: 100%;
    max-height: 150px;
  }

  .main {
    width: 100%;
    gap: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    .item {
      h2 {
        margin: 0;
        font-size: 1.4rem;
        line-height: 1.4rem;
      }
      width: 100%;
      gap: 1rem;
      div,
      img {
        width: 90%;
        @include tw-is-desktop {
          width: 50%;
        }
      }
      div:last-child {
        @include tw-is-desktop {
          text-align: right;
        }
      }
    }
  }
  .i-bi\:check {
    color: $success;
    width: 1.4rem;
    height: 1.4rem;
  }

  .accordion,
  .faq {
    width: 100%;
  }

  .terms {
    font-size: 0.9rem;
    margin: 0;
    text-align: center;
  }

  .form {
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
    position: sticky;
    bottom: 0;
    background-color: $white;
    width: 100%;
    border-top: 1px solid $lightGray;
    @include tw-is-desktop {
      position: relative;
      border-top: none;
    }
    form {
      width: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 1rem;
    }
  }

  .tw-select {
    padding: 0.25rem 1rem;
    width: 100%;
    border-color: $lightGray;
    border-radius: 0.222rem;
    font-size: 1.2rem;
    @include tw-is-desktop {
      width: auto;
    }
  }

  .tw-button {
    width: 100%;
    padding: 0.8rem;
    font-size: 1.2rem;
    font-weight: bold;

    @include tw-is-desktop {
      width: auto;
      padding: 1rem 4rem;
    }

    @include tw-color-set($white, $primary, $primary, transparent);

    &.is--hovered {
      @include tw-color-set($white, #d47d00, $primary-variant, $primary-variant);
    }
  }
}
</style>

<i18n lang="json">
{
  "de": {
    "publication": "Multi-Portal Veröffentlichung",
    "text_start": "Veröffentliche deine Anzeige mit einem Klick in Deutschlands größtem Tauschnetzwerk.",
    "accept_terms_of_use": "Mit Klick auf 'Jetzt kaufen' stimmst du den {url} zu.",
    "terms": "AGB",
    "buy-now": "Jetzt kaufen",
    "one-time": "einmalig",
    "days": "Tage",
    "unknown-error": "Ein unbekannter Fehler ist aufgetreten.",
    "unknown-error-message": "Bitte versuche es erneut. Sollte der Fehler weiterhin bestehen, kontaktiere bitte unseren Support.",
    "more-reach": "Erreiche Tausende Menschen sofort",
    "guaranteed-visibility": "Garantiert sichtbar auf allen Top-Portalen",
    "largest-exchange-network": "Deutschlands größtes Tauschnetzwerk",
    "largest-exchange-network-text": "Bei Tauschwohnung, bieten wir dir das größte Tauschnetzwerk mit unseren Partnern. Nur bei uns hast du die größten Chancen auf einen erfolgreichen Tausch.",
    "visibility": "Garantierte Sichtbarkeit",
    "visibility-text": "Dein Angebot wird während der Laufzeit garantiert auf den Top-Portale angezeigt, und nicht mehr per Zufall.",
    "convenient": "Einfach & bequem",
    "convenient-text": "Mit einem Klick und dem Kauf ist alles erledigt und ein paar Minuten später bist du überall zu sehen. Die Links zu den Anzeigen auf anderen Portalen werden dir in deinem Profil angezeigt.",
    "ten-times-reach": "10x mehr Reichweite",
    "ten-times-reach-text": "Weil deine Anzeige gleichzeitig und garantiert auf allen Top-Portalen erscheint, erhältst du bis zu 10x mehr Reichweite.",
    "xxx-days-for-x-currency": "{days} Tage für {price} {currency}"
  },
  "en": {
    "publication": "Multi-portal publishing",
    "text_start": "Publish your ad with one click in Germany's largest exchange network.",
    "accept_terms_of_use": "By clicking 'Buy now', you agree to our {url}.",
    "terms": "Terms and conditions",
    "buy-now": "Buy now",
    "one-time": "one-time",
    "days": "days",
    "unknown-error": "An unknown error occurred.",
    "unknown-error-message": "Please try again. If the error persists, please contact our support.",
    "more-reach": "Reach thousands of people instantly",
    "guaranteed-visibility": "Guaranteed visibility on all top portals",
    "largest-exchange-network": "Germany's largest exchange network",
    "largest-exchange-network-text": "At Tauschwohnung, we offer you the largest exchange network with our partners. Only with us do you have the greatest chances of a successful exchange.",
    "visibility": "Guaranteed Visibility",
    "visibility-text": "Your offer is guaranteed to be displayed on the top portals during the term and no longer by chance.",
    "convenient": "Simple & convenient",
    "convenient-text": "With one click and the purchase, everything is done and a few minutes later you are visible everywhere. The links to the ads on other portals will be displayed in your profile.",
    "ten-times-reach": "10x more reach",
    "ten-times-reach-text": "Because your ad appears simultaneously and guaranteed on all top portals, you get up to 10x more reach.",
    "xxx-days-for-x-currency": "{days} days for {price} {currency}"
  }
}
</i18n>
